import {SubscriptionClient} from "subscriptions-transport-ws";
import {WebSocketLink} from "@apollo/client/link/ws";
import {ApolloClient, ApolloLink, from, HttpLink, InMemoryCache, NormalizedCacheObject, split} from "@apollo/client";
import {BatchHttpLink} from "@apollo/client/link/batch-http";
import {getMainDefinition, relayStylePagination} from "@apollo/client/utilities";
import {createUploadLink} from "apollo-upload-client";
import {onError} from "@apollo/client/link/error";
import {GET_USER} from "./queries/GetUser";
import isSSR from "../utils/isSSR";
import {isEqual, merge} from "lodash";
import {useMemo} from "react";
import { IncomingHttpHeaders } from "http";

export const wsSubscriptionClient = isSSR() ? {
    client: null, close: () => {
    }
} : new SubscriptionClient((process.env.NEXT_PUBLIC_API_URL as string).replace("https", "wss") ?? "", {
    reconnect: true
});
const cleanTypeNameMiddleWare = new ApolloLink((operation, forward) => {
    if (operation.variables) {
        const omitTypename = (key: string, value: any) => (key === '__typename' ? undefined : value);
        operation.variables = JSON.parse(JSON.stringify(operation.variables), omitTypename);
    }
    return forward(operation).map((data) => {
        return data;
    });
});

const unAuthorizedLink = onError((error) => {
    try {
        if (error.graphQLErrors?.find(x => x.extensions['code'] == 'AUTH_NOT_AUTHENTICATED') != null) {
            (apolloClient as ApolloClient<NormalizedCacheObject>).cache.writeQuery({
                data: {identity: null},
                query: GET_USER
            });
        }
    } catch (e) {
    }
});

const createApolloClient = (headers: IncomingHttpHeaders | null = null) => {
    const enhancedFetch = (url: RequestInfo, init: RequestInit) => {
        return fetch(url, {
            ...init,
            headers: {
                ...init.headers,
                'Access-Control-Allow-Origin': '*',
                // here we pass the cookie along for each request
                Cookie: headers?.cookie ?? '',
                "User-Agent": headers?.["user-agent"] ?? isSSR()? '' : window?.navigator?.userAgent
            },
        }).then((response) => response)
    }
    const options = {
        uri: process.env.NEXT_PUBLIC_API_URL ?? "",
        credentials: 'include',
        fetchOptions: {
            mode: 'cors',
        },
        fetch: enhancedFetch

    }
    const batchHttpLink = from([cleanTypeNameMiddleWare, new BatchHttpLink({
        batchMax: 10, // No more than 10 operations per batch
        batchInterval: 20, // Wait no more than 20ms after first batched operation
        ...options
    })]);


    const httpLink = from([cleanTypeNameMiddleWare, new HttpLink({
        ...options
    })]);
    const wsLink = isSSR() ? httpLink : new WebSocketLink(
        wsSubscriptionClient as SubscriptionClient
    );
    const spliBatchHttpLink = split(
        ({query}) => {
            const definition = getMainDefinition(query);
            return (
                definition.kind === 'OperationDefinition' &&
                definition.operation === 'mutation'
            );
        },
        httpLink,
        batchHttpLink
    )

    const splitUploadHttpLink = split(
        operation => operation.getContext().hasUpload,
        createUploadLink(options),
        spliBatchHttpLink
    )

    const splitLink = split(
        ({query}) => {
            const definition = getMainDefinition(query);
            return (
                definition.kind === 'OperationDefinition' &&
                definition.operation === 'subscription'
            );
        },
        wsLink,
        splitUploadHttpLink,
    );

    return new ApolloClient({
        link: unAuthorizedLink.concat(splitLink),
        cache: new InMemoryCache(
            {
                typePolicies: {
                    Query: {
                        fields: {
                            gameHistory: relayStylePagination(),
                            gameHistoryDetails: relayStylePagination(["gameSessionId"]),
                            paymentHistory: relayStylePagination(),
                            blogPostsPaginated: relayStylePagination(),
                            tournamentHistory: relayStylePagination()
                        },
                    },
                },
            }
        ),
        ssrMode: isSSR(),
        ssrForceFetchDelay: 100,
    });
};

let apolloClient: ApolloClient<NormalizedCacheObject> | null = null;
export const APOLLO_STATE_PROP_NAME = '__APOLLO_STATE__'

export function initializeApollo({ headers, initialState } : {
    headers?: IncomingHttpHeaders | null
    initialState?: NormalizedCacheObject | undefined | null
}) {
    const _apolloClient = apolloClient ?? createApolloClient(headers)

    // If your page has Next.js data fetching methods that use Apollo Client, the initial state
    // get hydrated here
    if (initialState) {
        // Get existing cache, loaded during client side data fetching
        const existingCache = _apolloClient.extract()

        // Merge the existing cache into data passed from getStaticProps/getServerSideProps
        const data = merge(initialState, existingCache, {
            // combine arrays using object equality (like in sets)
            arrayMerge: (destinationArray: any, sourceArray: any) => [
                ...sourceArray,
                ...destinationArray.filter((d: any) =>
                    sourceArray.every((s: any) => !isEqual(d, s))
                ),
            ],
        })

        // Restore the cache with the merged data
        _apolloClient.cache.restore(data)
    }
    // For SSG and SSR always create a new Apollo Client
    if (isSSR()) return _apolloClient

    if (!apolloClient) apolloClient = _apolloClient
    return _apolloClient
}

export function addApolloState(client: any, pageProps: any) {
    if (pageProps?.props) {
        pageProps.props[APOLLO_STATE_PROP_NAME] = client.cache.extract()
    }

    return pageProps
}


export function useApollo(pageProps: any) {
    const state = pageProps[APOLLO_STATE_PROP_NAME]
    return useMemo(() => initializeApollo({initialState:state}), [state])
}